import { normalizeDecimal } from 'utils/numberUtils/decimalInput'
import { getLocaleNumberDisplay } from 'utils/numberUtils/numberUtils'
import type { LocaleNumberDisplay } from 'utils/numberUtils/numberUtils.types'
import type { CellCaretPlacement, CellEditOutcome, TableCellEditRule, TextSelection } from './HourlyDataTable.types'

const DIGIT = /\d/
const LAST_SEPARATOR = /[.,](?=[^.,]*$)/
const LEADING_SIGNS = ['+', '-']
const MINUS_SIGN = '-'
const FIRST_NON_SPACE = /\S/

type NumberCellEditRule = Extract<TableCellEditRule, { kind: 'number' }>

type CellEdit = {
  rule: TableCellEditRule
  storedValue: string
  editedText: string
  selectionBeforeEdit: TextSelection | undefined
}

type CaretEdit = {
  rule: TableCellEditRule
  editedText: string
  caret: number
  nextStoredValue: string
}

type ContiguousEdit = {
  keptPrefixLength: number
  keptSuffixLength: number
}

type NumericCellDisplay = LocaleNumberDisplay & {
  keptCharacters: string[]
}

type SignDeletionKey = Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey' | 'isComposing'>
type SignDeletionField = Pick<HTMLInputElement, 'value' | 'selectionStart' | 'selectionEnd'>

function findLastSeparator(text: string): string | undefined {
  return text.match(LAST_SEPARATOR)?.[0]
}

function buildNumericCellDisplay(storedValue: string, rule: NumberCellEditRule): NumericCellDisplay {
  const trimmed = storedValue.trim()
  const reading = normalizeDecimal(trimmed)
  const display = rule.admitsSign || !reading.startsWith('-') ? getLocaleNumberDisplay(reading) : null
  if (!display) {
    return {
      text: trimmed,
      isGrouping: Array.from({ length: trimmed.length }, () => false),
      keptCharacters: trimmed.split(''),
    }
  }

  const storedSeparator = findLastSeparator(trimmed)

  return {
    ...display,
    keptCharacters: display.text.split('').map((character, index) => {
      if (display.isGrouping[index]) {
        return ''
      }
      const isDecimalSeparator = !isDigit(character) && !(index === 0 && character === '-')
      return isDecimalSeparator ? (storedSeparator ?? character) : character
    }),
  }
}

function getKeptText(display: NumericCellDisplay, start: number, end: number): string {
  return display.keptCharacters.slice(start, end).join('')
}

export function getCellDisplay(storedValue: string, rule: TableCellEditRule): string {
  return rule.kind === 'number' ? buildNumericCellDisplay(storedValue, rule).text : storedValue
}

function findContiguousEdit(before: string, after: string, selection: TextSelection | undefined): ContiguousEdit {
  const sharedLength = Math.min(before.length, after.length)
  const maxPrefixLength = Math.min(sharedLength, selection?.start ?? sharedLength)
  let keptPrefixLength = 0
  while (keptPrefixLength < maxPrefixLength && before[keptPrefixLength] === after[keptPrefixLength]) {
    keptPrefixLength += 1
  }

  const maxSuffixLength = Math.min(
    sharedLength - keptPrefixLength,
    selection ? Math.max(before.length - selection.end, 0) : sharedLength
  )
  let keptSuffixLength = 0
  while (
    keptSuffixLength < maxSuffixLength &&
    before[before.length - 1 - keptSuffixLength] === after[after.length - 1 - keptSuffixLength]
  ) {
    keptSuffixLength += 1
  }

  return { keptPrefixLength, keptSuffixLength }
}

function getInsertedText(editedText: string, { keptPrefixLength, keptSuffixLength }: ContiguousEdit): string {
  return editedText.slice(keptPrefixLength, editedText.length - keptSuffixLength)
}

function toCellEditOutcome(storedValue: string, value: string, insertedText: string): CellEditOutcome {
  return value === storedValue ? { kind: 'unchanged', insertedText } : { kind: 'changed', value, insertedText }
}

function acceptsInsertedText(rule: NumberCellEditRule, insertedText: string): boolean {
  const { acceptsCharacter } = rule
  return acceptsCharacter === undefined || Array.from(insertedText).every((character) => acceptsCharacter(character))
}

function countMisplacedSigns(text: string): number {
  const valueStart = text.search(FIRST_NON_SPACE)
  return text.split('').filter((character, index) => character === MINUS_SIGN && index > valueStart).length
}

function isRefusedWhereverItLands(rule: NumberCellEditRule, insertedText: string): boolean {
  return !acceptsInsertedText(rule, insertedText) || (rule.admitsSign && countMisplacedSigns(insertedText) > 0)
}

function placesSignBeyondStart(storedValue: string, value: string, insertedText: string): boolean {
  const misplacedSigns = countMisplacedSigns(value)
  return misplacedSigns > 0 && (insertedText.includes(MINUS_SIGN) || misplacedSigns > countMisplacedSigns(storedValue))
}

export function getEditedCellValue({ rule, storedValue, editedText, selectionBeforeEdit }: CellEdit): CellEditOutcome {
  if (rule.kind === 'text') {
    const insertedText = getInsertedText(editedText, findContiguousEdit(storedValue, editedText, selectionBeforeEdit))
    return toCellEditOutcome(storedValue, editedText, insertedText)
  }

  const display = buildNumericCellDisplay(storedValue, rule)
  const displayedText = display.text
  const contiguousEdit = findContiguousEdit(displayedText, editedText, selectionBeforeEdit)
  const { keptPrefixLength, keptSuffixLength } = contiguousEdit
  const insertedText = getInsertedText(editedText, contiguousEdit)
  const restore = { text: displayedText, start: keptPrefixLength, end: displayedText.length - keptSuffixLength }
  if (isRefusedWhereverItLands(rule, insertedText)) {
    return { kind: 'refused', restore }
  }

  const value = [
    getKeptText(display, 0, keptPrefixLength),
    insertedText,
    getKeptText(display, displayedText.length - keptSuffixLength, displayedText.length),
  ].join('')

  if (rule.admitsSign && placesSignBeyondStart(storedValue, value, insertedText)) {
    return { kind: 'refusedUnlessReplacing', insertedText, restore }
  }

  return toCellEditOutcome(storedValue, value, insertedText)
}

function isDigit(character: string | undefined): boolean {
  return character !== undefined && DIGIT.test(character)
}

function countDigits(text: string): number {
  return text.split('').filter(isDigit).length
}

function getPositionAfterDigits(display: LocaleNumberDisplay, digitCount: number): number {
  let position = 0
  let digitsPassed = 0
  while (digitsPassed < digitCount && position < display.text.length) {
    if (isDigit(display.text[position])) {
      digitsPassed += 1
    }
    position += 1
  }

  return position
}

export function getCellCaretAfterEdit({
  rule,
  editedText,
  caret,
  nextStoredValue,
}: CaretEdit): CellCaretPlacement | null {
  if (rule.kind === 'text') {
    return null
  }

  const display = buildNumericCellDisplay(nextStoredValue, rule)
  if (display.text === editedText) {
    return null
  }

  const position = getPositionAfterDigits(display, countDigits(editedText.slice(0, caret)))
  const editedBeforeCaret = editedText[caret - 1]
  const displayAtCaret = display.text[position]
  const followsEditedNonDigit =
    editedBeforeCaret !== undefined &&
    displayAtCaret !== undefined &&
    !isDigit(editedBeforeCaret) &&
    !isDigit(displayAtCaret) &&
    !display.isGrouping[position]

  const placedCaret = followsEditedNonDigit ? position + 1 : position

  return { text: display.text, start: placedCaret, end: placedCaret }
}

export function isDeleteOfLeadingSign(
  rule: TableCellEditRule,
  key: SignDeletionKey,
  field: SignDeletionField
): boolean {
  if (rule.kind !== 'number' || !rule.admitsSign) {
    return false
  }
  if (key.key !== 'Delete' || key.ctrlKey || key.altKey || key.metaKey || key.shiftKey || key.isComposing) {
    return false
  }
  if (!LEADING_SIGNS.includes(field.value.charAt(0)) || field.selectionStart !== 0) {
    return false
  }

  return field.selectionEnd === 0 || field.selectionEnd === 1
}
